//CommunityInfo.ts =================================================
import "../Util/Prototypes";

import * as Custom from "../Custom";
import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as ExpandOnPhone from "./ExpandOnPhone";
import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as Spinner from "../Util/Spinner";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Widgets from "./Widgets";

import { Accordion, AccordionDetails, AccordionSummary, Button, CssBaseline, Icon, IconButton, Link, 
   Paper, Typography } from "@mui/material";

import { ThemeProvider } from "@mui/material/styles";

import React from "react";
import ReactDOM from "react-dom/client";

import Extent from "@arcgis/core/geometry/Extent";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Graphic from "@arcgis/core/Graphic";
import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";

export let centerPnt: __esri.Point; //If not null, then execute with this point.
export let descriptionText: string = null;

//Setters.
export function setCenterPnt(pnt: __esri.Point) {
   centerPnt = pnt;
}

const toolName: string = "Community Information";
const maxExpansionPanels: number = 40;

let click_eventHandler: IHandle;

//Module variables.
let ii: number; //Index into the community info settings array.
let query: __esri.Query;
let clickPnt: __esri.Point; // Point geometry of users click.
let ciSpec: Settings.iCommunityInfo;
let laySpec: Settings.iLayer;
let showHelp: boolean = true;
let expanded: boolean[] = [];
let expandedSave: boolean[] = [];
let parcelPoly: __esri.Polygon | __esri.Polygon[];
let printing: boolean = false;

interface iResult {
   heading: string;
   details: iDetail[];
}
let prevResult: iResult;

interface iDetail {
   description: string;
   geom: __esri.Geometry; //Geometry of feature.
   fields: Util_App.iField[]; //Field values.
}
let results: iResult[] = [];

//Clear this tool.
export function clearTool() {
}

//Input arguments.
interface iProps {
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of CommunityInfo.Render");

   //Wire up component for each time render is called, but not on refresh.
   const [initComponent, setState] = React.useState(() => {

      //Initialize expanded array.
      if (expanded.length === 0) {
         for (let i: number = 0; i < maxExpansionPanels; i++) {
            expanded.Add(false);
         }
      }
   });

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      //MainMap.activeViewDiv.style.cursor = "pointer";
      click_eventHandler = MainMap.activeView.on("click", function (event: __esri.ViewClickEvent) {
         if (DrawerMenu.curToolName === "Community Information") {
            clickPnt = event.mapPoint;
            init();
         }
      });

      //Component will unmount.
      return () => {
         //if (DrawerMenu.curToolName = "Community Information") {
         //   MainMap.activeViewDiv.style.cursor = "default";
         //}
         click_eventHandler.remove();

         GraLay.temp.removeAll();
         GraLay.clearIdentifyHighlightGraphics();
      };
   }, []);

   //Component did update.
   React.useEffect(() => {
      if (centerPnt) {
         clickPnt = centerPnt.clone(); //Process from external link.
         init();
         centerPnt = null;

      } else if (printing) {
         printPreview(); //Print page has been rendered.

      } else {
         Widgets.setPosition();
      }
   });

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Start community info search.
   function init() {
      Util.logMsg("Top of CommunityInfo.init");
      if (Spinner.busy) {
         return; //Quit if busy.
      }
      Spinner.show(true);

      //Init.
      ii = -1;
      showHelp = false;
      prevResult = null;
      results = [];

      //Add point graphic.
      const sym = {
         type: "picture-marker",
         url: "Images/Community.png",
         width: 18, height: 18, xoffset: 0, yoffset: 4,
      } as __esri.PictureMarkerSymbol;

      const gra = new Graphic({ geometry: clickPnt, symbol: sym });
      GraLay.temp.removeAll();
      GraLay.clearIdentifyHighlightGraphics();
      GraLay.temp.add(gra);

      //Start the community info process.
      processNextSetting();
   }

   //Process one setting at a time.
   function processNextSetting(): void {
      //Util.logMsg("Top of CommunityInfo.processNextSetting");
      ii++;

      if (ii < Settings.communityInfo.length) {
         ciSpec = Settings.communityInfo[ii];
         laySpec = Util_App.getLayerSpec(ciSpec.layerName);

         Util.logMsg("Processing Next setting layer: " + ciSpec.layerName + "->" + ciSpec.fieldNames[0]);

         if (!laySpec) {
            Util.errMsg("Layer setting is missing for community info layer: " + ciSpec.layerName);
            processNextSetting();

         } else if (!laySpec.mapServiceURL) {
            Util.errMsg("Layer setting is missing the map service: " + ciSpec.layerName);
            processNextSetting();

         } else if (ciSpec.searchMode === "pointinpoly") {
            processPointInPoly();

         } else if (ciSpec.searchMode === "nearestpoint") {
            processNearbyFeatures();

         } else if (ciSpec.searchMode === "parceloverlay") {
            processPolygonOverlay();

         } else {
            Util.errMsg("Invalid community info search mode: " + ciSpec.searchMode);
            processNextSetting();
         }

      } else { //All settings have been processed.
         displayResults(); //Build results.
      }
   }

   //Process point in polygon mode.
   function processPointInPoly() {
      Util.logMsg("Top of CommunityInfo.processPointInPoly: " + laySpec.layerName);

      //Build query.
      query = new Query({
         returnGeometry: true,
         outSpatialReference: MainMap.activeView.spatialReference,
         geometry: clickPnt,
         maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
         outFields: ["*"],
      });

      if (!MainMap.activeView.spatialReference.isWebMercator) {
         query.geometryPrecision = 0;
      }

      const url: string = Util_App.getLayerURL(laySpec);
      QueryTask.executeQueryJSON(url, query).then(pointInPoly_success).catch(queryTask_failed);
   }

   //Process point in polygon results.
   function pointInPoly_success(fs: __esri.FeatureSet) {
      Util.logMsg("Top of CommunityInfo.pointInPoly_success");

      if (fs.features.length > 0) {
         saveResult(ciSpec.description, fs, fs.features[0]);

      } else { //No features found.
         saveResult(ciSpec.description, fs);
      }
      processNextSetting();
   }

   //Get nearest point information.
   function processNearbyFeatures() {
      Util.logMsg("Top of CommunityInfo.processNearbyFeatures");

      //Build query.
      query = new Query({
         returnGeometry: true,
         outSpatialReference: MainMap.activeView.spatialReference,
         geometry: clickPnt,
         maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
         outFields: ["*"],
         distance: ciSpec.radius * 5280,
         units: "feet",
      });

      if (!MainMap.activeView.spatialReference.isWebMercator) {
         query.geometryPrecision = 0;
      }

      const url: string = Util_App.getLayerURL(laySpec);
      QueryTask.executeQueryJSON(url, query).then(getNearPntOverlay_Completed).catch(queryTask_failed);
   }

   //Get nearest point overlay polygons completed.
   function getNearPntOverlay_Completed(fs: __esri.FeatureSet) {
      Util.logMsg("Top of CommunityInfo.getNearPntOverlay_Completed");
      let description: string = ciSpec.description;
      let firstTime: boolean = true;

      if (fs.features.length > 0) {
         const nearList: Util_App.iNearFeatures[] = Util_App.getNearestFeatures(fs, clickPnt, ciSpec.maxResults);

         for (const rec of nearList) {
            if (!firstTime) {
               description = "Also nearby";
            }
            const distText: string = Util_App.distanceToText(rec.distance);
            if (!ciSpec.hideMeasurement) {
               description += " (" + distText + ")";
            }
            saveResult(description, fs, rec.feature);
            firstTime = false;
         }

      } else { //No features found.
         saveResult(description, fs);
      }

      processNextSetting();
   }

   //Process polygon overlay mode.
   function processPolygonOverlay() {
      Util.logMsg("Top of CommunityInfo.processPolygonOverlay: " + Settings.parcelLayerName);

      //Build query.
      query = new Query({
         returnGeometry: true,
         outSpatialReference: MainMap.activeView.spatialReference,
         geometry: clickPnt,
         maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
         outFields: [Settings.pin_fieldName],
      });

      if (!MainMap.activeView.spatialReference.isWebMercator) {
         query.geometryPrecision = 0;
      }

      const parcelLaySpec: Settings.iLayer = Util_App.getLayerSpec(Settings.parcelLayerName);
      const url: string = Util_App.getLayerURL(parcelLaySpec);
      QueryTask.executeQueryJSON(url, query).then(getParcelPolygon_success).catch(queryTask_failed);
   }

   //Get parcel polygon success.
   function getParcelPolygon_success(fs: __esri.FeatureSet) {
      Util.logMsg("Top of CommunityInfo.getParcelPolygon_success");

      if (fs.features.length > 0) {
         const parcelFeature = fs.features[0];

         //Buffer the parcel poly to create an inside smaller polygon to avoid sliver issues.
         if (MainMap.activeView.spatialReference.isWebMercator) {
            parcelPoly = geometryEngine.geodesicBuffer(parcelFeature.geometry, -Settings.parcelInsideBufferDistance, "feet", true);
         } else {
            parcelPoly = geometryEngine.buffer(parcelFeature.geometry, -Settings.parcelInsideBufferDistance, "feet", true);
         }

         //Build query to get intersecting features.
         query = new Query({
            returnGeometry: true,
            outSpatialReference: MainMap.activeView.spatialReference,
            geometry: parcelPoly as __esri.Geometry,
            maxAllowableOffset: Util_App.feetToMeters(Settings.maxAllowableOffset),
            outFields: ["*"],
         });

         if (!MainMap.activeView.spatialReference.isWebMercator) {
            query.geometryPrecision = 0;
         }

         const url: string = Util_App.getLayerURL(laySpec);
         QueryTask.executeQueryJSON(url, query).then(getOverlayFeatures_success).catch(queryTask_failed);

      } else { //No features found.
         saveResult(ciSpec.description, fs);
         processNextSetting();
      }
   }

   //Get overlay features success.
   function getOverlayFeatures_success(fs: __esri.FeatureSet) {
      Util.logMsg("Top of CommunityInfo.getOverlayFeatures_success");

      if (fs.features.length > 0) {
         let orderClickFirst: boolean = true;
         if (ciSpec.hideMeasurement && ciSpec.maxResults === 99999) {
            orderClickFirst = false;
         }

         let overlayFeatures: Util_App.iOverlayfeatures[] = Util_App.getClippedOverlayFeatures(fs.features, parcelPoly as __esri.Polygon, "square-feet", clickPnt, orderClickFirst);

         //Comment out later. (for debugging only)
         //GraLay.temp.removeAll();
         //for (const rec of overlayFeatures) {
         //   const gra = rec.feature.clone();
         //   gra.symbol = Settings.identifyHighlightPolygonSym;
         //   GraLay.temp.add(gra);
         //}

         //Combine like polygon features.
         if (overlayFeatures[0].feature.geometry.type === "polygon") {
            let markedForDelete: boolean[] = [];
            for (const loop of overlayFeatures) {
               markedForDelete.Add(false); //Initialize marked for delete records.
            }

            for (let i: number = 0; i < overlayFeatures.length; i++) {
               if (!markedForDelete[i]) {
                  const rec1: Util_App.iOverlayfeatures = overlayFeatures[i];
                  let totalArea: number = rec1.areaOrDistance;

                  for (let j: number = i + 1; j < overlayFeatures.length; j++) {
                     const rec2: Util_App.iOverlayfeatures = overlayFeatures[j];

                     if (fieldValuesMatch(fs, rec1, rec2)) {
                        totalArea += rec2.areaOrDistance;
                        markedForDelete[j] = true;
                     }
                  }

                  rec1.areaOrDistance = totalArea;
               }
            }

            //Remove duplicate features.
            const overlayFeats: Util_App.iOverlayfeatures[] = [];

            for (let m: number = 0; m < overlayFeatures.length; m++) {
               if (!markedForDelete[m]) {
                  overlayFeats.Add(overlayFeatures[m]);
               }
            }
            overlayFeatures = overlayFeats;
         }

         //Show feature at clicked point.
         const rec0: Util_App.iOverlayfeatures = overlayFeatures[0];

         let labelText: string = Util_App.distanceToText(rec0.areaOrDistance);
         if (rec0.feature.geometry.type === "polygon") {
            labelText = Util_App.areaToText(rec0.areaOrDistance);
         }

         let description: string = ciSpec.description;
         if (!ciSpec.hideMeasurement) {
            description += " (" + labelText + ")";
         }

         saveResult(description, fs, rec0.feature);

         //Show remaining features.
         let k: number = 0;

         for (const rec of overlayFeatures) {
            if (k > 0 && k < ciSpec.maxResults) {

               labelText = Util_App.distanceToText(rec.areaOrDistance);
               if (rec0.feature.geometry.type === "polygon") {
                  labelText = Util_App.areaToText(rec.areaOrDistance);
               }

               description = "Also on parcel";
               if (!ciSpec.hideMeasurement) {
                  description += " (" + labelText + ")";
               }
               saveResult(description, fs, rec.feature);
            }
            k++;
         }

         processNextSetting();

      } else { //No features found.
         saveResult(ciSpec.description, fs);
         processNextSetting();
      }
   }

   //Check if field values 1 and 2 match.
   function fieldValuesMatch(fs: __esri.FeatureSet, rec1: Util_App.iOverlayfeatures, rec2: Util_App.iOverlayfeatures): boolean {
      let value1: string = Util_App.getFieldValueNoCase(ciSpec.fieldNames[0], rec1.feature, fs) as string;
      let value2: string = Util_App.getFieldValueNoCase(ciSpec.fieldNames[0], rec2.feature, fs) as string;

      if (ciSpec.fieldNames.length > 1) {
         value1 += "+" + Util_App.getFieldValueNoCase(ciSpec.fieldNames[1], rec1.feature, fs);
         value2 += "+" + Util_App.getFieldValueNoCase(ciSpec.fieldNames[1], rec2.feature, fs);
      }
      if (value1 === value2) {
         return true;
      }
      return false;
   }

   //Save a result.
   function saveResult(description: string, fs: __esri.FeatureSet, feature?: __esri.Graphic) {
      Util.logMsg("Top of CommunityInfo.saveResult");

      //Save results.
      let result: iResult;
      const emptyField: Util_App.iField = { name: "", alias: "", value: "", url: "", formatType: "", dataType: "string", accessLevel: 0 };

      const detail: iDetail = {
         description: description, geom: null, fields: [emptyField, emptyField],
      };

      //Use existing result record.
      if (prevResult && prevResult.heading === ciSpec.heading) {
         result = prevResult;
         result.details.Add(detail);

      } else { //Start result heading panel.
         refresh(); //Refresh left panel after each heading is completed.
         result = { heading: ciSpec.heading, details: [detail] };
         results.Add(result);
      }

      //Set field values.
      if (feature && feature.geometry) {
         detail.geom = feature.geometry;

         //Two times one for each setting field.
         for (let i: number = 0; i < ciSpec.fieldNames.length; i++) {
            if (ciSpec.fieldNames[i] !== "") {
               descriptionText = description; //So description can be used in Custom.ts

               const fields: Util_App.iField[] = Util_App.getFeatureValues(fs, feature, laySpec.layerName,
                  ciSpec.fieldNames[i], ciSpec.hyperLinkFields[i]);

               descriptionText = null;

               if (fields.length > 0) {
                  detail.fields[i] = fields[0];
               }
            }
         }

      } else { //Set not available text.
         if (detail.fields[0].value.toString().trim() === "") {
            detail.fields[0].value = Custom.getCustomNoValueText(description);
         }
      }

      prevResult = result;
   }

   //Flush out final result.
   function displayResults(): void {
      Util.logMsg("Top of CommunityInfo.displayResults");
      Spinner.hide();
      showHelp = true;
      refresh();
   }

   //Query task failed.
   function queryTask_failed(error: Error): void {
      let msg: string = "QueryTask failed.\n ";
      Spinner.hide();

      if (query.where) {
         msg += "Where: " + query.where + " ";
      }
      if (query.outFields) {
         msg += "Output fields: " + query.outFields.toString() + " ";
      }
      errMsg(msg, error);
   }

   //Show error message.
   function errMsg(msg: string, error?: object): void {
      if (error) {
         Util.errMsg(msg, error);
      } else {
         Util.errMsg(msg);
      }
      Spinner.hide();
   }

   //Zoom to feature.
   function zoomTo(geom: __esri.Geometry): void {
      //Highlight the feature.
      GraLay.clearIdentifyHighlightGraphics();

      let sym: __esri.SimpleMarkerSymbol | __esri.SimpleLineSymbol | __esri.SimpleFillSymbol = Settings.identifyHighlightPointSym;
      if (geom.type === "polyline") {
         sym = Settings.identifyHighlightLineSym;

      } else if (geom.type === "polygon") {
         sym = Settings.identifyHighlightPolygonSym;
      }

      const gra = new Graphic({ geometry: geom, symbol: sym });
      GraLay.identifyPoint.add(gra);

      //Zoom to feature.
      let mbr = geom.extent;
      if (!mbr) {
         if (geom.type === "point") {
            const pnt = geom as __esri.Point;
            mbr = new Extent({ xmin: pnt.x, ymin: pnt.y, xmax: pnt.x, ymax: pnt.y, spatialReference: MainMap.activeView.spatialReference });
         }
      }

      mbr = Util_App.addMarginToMBR(mbr, true);
      MainMap.activeView.goTo(mbr, { duration: 0 });
   }

   //1) Render community info to print div.
   function handlePrint() {
      printing = true;
      showHelp = false;
      expandedSave = expanded.slice(0); //Clone array for restoring after print.

      for (let i: number = 0; i < maxExpansionPanels; i++) {
         expanded[i] = true;
      }

      if (!Settings.printDivRoot) {
         Settings.setPrintDivRoot(ReactDOM.createRoot(document.getElementById("print_div")));
      }
      Settings.printDivRoot.render(<Render />);
   }

   //2) Show print preview.
   function printPreview() {
      printing = false;
      showHelp = true;
      expanded = expandedSave.slice(0); //Clone back to original.
      window.print();
      //ReactDOM. render(<div />, document.getElementById("print_div")); //Clear the print div.
      Widgets.show("Community Information"); //Force re-render in Widgets.tsx
   }

   //Expansion panel click.
   function expandClicked(i: number) {
      expanded[i] = !expanded[i]; //Toggle it.
      refresh();
   }

   //Build expansion panel.
   function getExpansionPanel(result: iResult, i: number): JSX.Element[] {
      const jsx: JSX.Element[] = [];

      const styles: Default.iStyles = {
         expansionPanelMain: { backgroundColor: Settings.leftPanelExpansionColor },
         expansionPanelSummary: { paddingLeft: 15, paddingRight: 15, fontWeight: 500 }, //Must have padding right 15
         expansionPanelDetails: { padding: '1px 0px 9px 0px', backgroundColor: Settings.paperColor, fontWeight: 500 },
         table: { width: '100%', overflowX: 'auto' },
      };

      if (printing) {
         styles.expansionPanelSummary.height = 28;
         styles.expansionPanelSummary.minHeight = 28;
      }

      //Create expansion panel.
      jsx.push(
         <Accordion key={Util.uid()} TransitionProps={{ unmountOnExit: true }} expanded={expanded[i]} style={styles.expansionPanelMain}>
            <AccordionSummary onClick={() => expandClicked(i)} expandIcon={<Icon>arrow_drop_down</Icon>} style={styles.expansionPanelSummary}>
               {result.heading}
            </AccordionSummary>

            <AccordionDetails style={styles.expansionPanelDetails}>
               <table style={styles.table}>
                  <tbody style={styles.body}>
                     {result.details.map(function (detail: iDetail, j: number) {
                        return (getDetailRow(detail, j));
                     })}
                  </tbody>
               </table>
            </AccordionDetails>
         </Accordion>,
      );

      return jsx;
   }

   //Build detail row.
   function getDetailRow(detail: iDetail, i: number): JSX.Element[] {
      const jsx: JSX.Element[] = [];

      let showZoomTo: boolean = false;
      if (detail.geom) {
         showZoomTo = true;
      }

      const value1 = getTableRowValue(detail.fields[0]);
      const value2 = getTableRowValue(detail.fields[1]);
      let hasValue2: boolean = false;
      if (detail.fields[1].name) {
         hasValue2 = true;
      }

      const styles: Default.iStyles = {
         div: { marginTop: 7 },
         bodyCellLabel: { textAlign: 'center', lineHeight: '1.15rem', padding: 3, color: 'black', fontSize: Settings.detailsFontSize, borderTop: '1px solid silver' },
         zoomTo: { width: 100, paddingLeft: 2, paddingRight: 2, backgroundColor: Settings.leftPanelLabelColor, lineHeight: 1.5 },
         zoomToNo: { width: 100, fontWeight: 400 },
         bodyCellValue: { width: (Settings.leftPanelWidth - 100), lineHeight: '1.15rem', padding: 3, textAlign: 'left', paddingLeft: 5, color: 'black', fontSize: Settings.detailsFontSize, borderTop: '1px solid silver' },
      };

      if (printing) {
         styles.zoomTo.width = 320;
         styles.zoomToNo.width = 320;
         styles.bodyCellValue.width = Settings.leftPanelWidth;
      }

      //Create detail row.
      jsx.push(
         <tr key={Util.uid()}>
            <td style={styles.bodyCellLabel}>
               {showZoomTo &&
                  <Button style={styles.zoomTo} color="info" variant="outlined" onClick={() => zoomTo(detail.geom)} fullWidth>
                     {detail.description}
                  </Button>
               }
               {!showZoomTo && <div style={styles.zoomToNo}>{detail.description}</div>}
            </td>
            <td style={styles.bodyCellValue}>
               <div>{value1}</div>
               {hasValue2 && <div style={styles.div}>{value2}</div>}
            </td>
         </tr>,
      );

      return jsx;
   }

   //Get table row value.
   function getTableRowValue(field: Util_App.iField): JSX.Element[] {
      const jsx: JSX.Element[] = [];

      if (!field.url) {
         jsx.push(
            <div key={Util.uid()}>{field.value}</div>,
         );

      } else {
         if (field.url && field.url.StartsWith("js:")) {
            jsx.push(
               <a key={Util.uid()} href="#" onClick={() => Custom.callFunction(field.url)}>{field.value}</a>,
            );

         } else {
            jsx.push(
               <Typography key={Util.uid()}>
                  <Link style={{ color: "blue", padding: 7, fontWeight: 500 }} href={field.url} target="_blank"
                     aria-label={field.value as string}>
                     {field.value}
                  </Link>
               </Typography>,
            );
         }
      }

      return jsx;
   }

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   let printIconVisible: boolean = false;
   if (!printing && results.length > 0 && showHelp) {
      printIconVisible = true;
   }

   let closeIconVisible: boolean = false;
   if (!printing) {
      closeIconVisible = true;
   }

   //Styles.
   const styles2: Default.iStyles = {
      help: { marginBottom: 7 },
      printStyle: { width: '100%', padding: 5, overflow: 'auto' },
   };

   let resultsStyle = Default.styles.appToolDiv;
   if (printing) {
      resultsStyle = styles2.printStyle;
   }

   //ThemeProvider is required for printing.
   return (
      <ThemeProvider theme={Default.theme}>
         <CssBaseline />

         <Paper>
            <div style={Default.styles.appToolHeading}>
               <Typography variant="body1" style={{ marginLeft: 12 }}>
                  {toolName}
               </Typography>

               <ExpandOnPhone.Render />

               {printIconVisible &&
                  <IconButton color="default" onClick={handlePrint} title="Print community information" aria-label="Print" >
                     <Icon>print</Icon>
                  </IconButton>
               }

               {closeIconVisible &&
                  <IconButton color="default" onClick={close} title="Close" aria-label="Close">
                     <Icon>close</Icon>
                  </IconButton>
               }
            </div>

            <div id={scrollDivID} style={resultsStyle}>
               {showHelp && <Typography style={styles2.help}>Click on the map for community information</Typography>}

               {results.map(function (result: iResult, i: number) {
                  return (getExpansionPanel(result, i));
               })}
            </div>
         </Paper>
      </ThemeProvider>
   ); //end return.

} //end function.
